import React, { FunctionComponent } from "react";
import { useParams } from "react-router-dom";
import { QueryClientProvider, useQuery } from "react-query";
import axios from "axios";
import { Box, Grid, Stack } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { ASectionTitle } from "@atiautomacao/ati-ui-library";
import { sharedQueryClient } from "../../../queryClient";
import { useAppSelector } from "../../../Config/Hooks";
import { getIconColor } from "../../../Shared/Components/Layout/menu/helpers/getIconColor";
import { MenuItemStatus } from "../../../Shared/Components/Layout/menu/types/MenuItemStatus";
import BessSocChart from "./BessSocChart";
import BessPowerChart from "./BessPowerChart";
import BessPcsModulesSection from "./BessPcsModulesSection";
import ActiveAlarms from "../Powerplant/ActiveAlarms/ActiveAlarms";
import BessDetailTotalizers from "./BessDetailTotalizers";
import BessBmsSocSection from "./BessBmsSocSection";
import BessInstantInfoSection from "./BessInstantInfoSection";
import { useBessDetailAutoRefresh } from "./useBessDetailAutoRefresh";

// Nome do equipamento (cabeçalho) via endpoint existente do main-api.
const fetchEquipmentName = async (equipmentId: number): Promise<string> => {
    const res = await axios.get(`api/equipment/${equipmentId}`, { timeout: 30000 });
    return res?.data?.data?.name ?? "BESS";
};

const BessDetailContent: FunctionComponent = () => {
    const { equipmentId: equipmentIdParam } = useParams<{ equipmentId: string }>();
    const equipmentId = Number(equipmentIdParam);

    // Equipamento selecionado na tela de Baterias (SGD-3652) — traz nome e status (etsStatus).
    const selectedEquipment = useAppSelector((state: any) => state.dashboard?.selectedEquipment);
    const selectedPowerPlant = useAppSelector((state: any) => state.dashboard?.selectedPowerPlant);

    // Number(undefined) === NaN, reprovado adiante por Number.isFinite.
    const powerPlantId = Number(selectedPowerPlant?.id);

    const { data: equipmentName } = useQuery({
        queryKey: ["bess-equipment-name", equipmentId],
        queryFn: () => fetchEquipmentName(equipmentId),
        enabled: Boolean(equipmentId) && !selectedEquipment?.name,
    });

    useBessDetailAutoRefresh();

    const displayName = selectedEquipment?.name ?? equipmentName ?? "BESS";
    const statusColor = getIconColor((selectedEquipment?.etsStatus ?? "") as MenuItemStatus);

    const isValidEquipment = Number.isFinite(equipmentId) && equipmentId > 0;
    const isValidPowerPlant = Number.isFinite(powerPlantId) && powerPlantId > 0;

    return (
        <Box sx={{ padding: "16px" }}>
            {/* Header padrão (ASectionTitle, como na tela Daily Report) + ícone de status ao lado do nome. */}
            <Stack direction="row" alignItems="center" spacing={1}>
                <ASectionTitle
                    titleLabel={displayName}
                    titleIcon={
                        <FiberManualRecordIcon
                            aria-label="bess-status"
                            sx={{ color: statusColor, fontSize: 24, marginRight: 1 }}
                        />
                    }
                />
            </Stack>

            <Grid container spacing={2} sx={{ mt: 1 }}>
                {/* Totalizadores (SGD-3682), no topo da tela — antes dos gráficos. */}
                <Grid item xs={12}>
                    <BessDetailTotalizers equipmentId={equipmentId} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <BessSocChart equipmentId={equipmentId} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <BessPowerChart equipmentId={equipmentId} />
                </Grid>
                {/* Cards de Informações Instantâneas (SGD-3735), abaixo dos 2 gráficos. */}
                <Grid item xs={12}>
                    <BessInstantInfoSection equipmentId={equipmentId} />
                </Grid>
                {/* Seção "SOC por Rack de Baterias" (SGD-3683), acima do PCS. */}
                <Grid item xs={12}>
                    <BessBmsSocSection equipmentId={equipmentId} />
                </Grid>
                {/* Seção "Módulos do Inversor PCS" (SGD-3677), abaixo dos gráficos. */}
                <Grid item xs={12}>
                    <BessPcsModulesSection equipmentId={equipmentId} />
                </Grid>
            </Grid>

            {isValidPowerPlant && isValidEquipment && (
                <ActiveAlarms
                    powerPlantId={powerPlantId}
                    equipmentId={equipmentId}
                    columnPreset="equipment-scoped"
                />
            )}
        </Box>
    );
};

const BessDetailPage: FunctionComponent = () => {
    return (
        <QueryClientProvider client={sharedQueryClient}>
            <BessDetailContent />
        </QueryClientProvider>
    );
};

export default BessDetailPage;
